/* El mapa completo de la aplicación, agrupado como se organiza la empresa.
 *
 * DOS NIVELES, Y EL DE ARRIBA SON LOS UNIVERSOS. Antes los grupos eran
 * "Operación", "Flota", "Gente", "Comercial", "Depósito", "Registro" — categorías
 * de datos, no de trabajo. Alguien de portería tenía lo suyo repartido entre
 * "Operación" y "Registro", y el de tráfico buscaba el mapa en un grupo y los
 * movimientos en otro. Ahora el primer nivel es cómo se llaman los sectores
 * adentro de la empresa:
 *
 *   Tráfico    operación, satelital, combustible y documentación
 *   Portería   entrada y salida de vehículos y personas, y las cargas
 *   Taller     la ticketera de reparaciones
 *   Puerto     fichaje, trabajos y novedades del turno
 *   Padrones   los archivos: quién y qué existe
 *   RRHH       la nómina de sueldos y lo que no coincide con el padrón
 *   Pañol      una sede por hijo, los padrones y los movimientos
 *   Presidencia el tablero comercial que mira Gabriel
 *   Dirección  dos pantallas, dos personas
 *
 * Se llama Pañol y no Depósito porque es como se lo nombra: el pañol es el
 * lugar y el pañolero la persona. "Comercial" desapareció — no existe ese
 * sector, y lo único que colgaba de ahí (el padrón de proveedores, que desde
 * el 20/10 se llama «Talleres y servicios») es del pañol.
 *
 * Cada sección tiene un `donde`:
 *
 * `corto` es el rótulo para la barra de abajo del celular, y sólo está donde
 * el largo no entra en una columna de un cuarto de pantalla ("Ingreso y
 * egreso" → "Barrera"). Donde no está se usa el `label` de siempre: un segundo
 * nombre para cada pantalla serían veinticinco rótulos más que mantener, y el
 * día que difieran el menú y la barra dirían cosas distintas de lo mismo.
 *
 *   "aca"      una pantalla de esta app          → link normal
 *   "flotabot" una página del panel de FlotaBot  → se muestra ACÁ ADENTRO, en
 *              /panel/<id>, sin sacar a nadie de la ventana
 *   "pronto"   todavía no existe                 → se ve, pero no se puede tocar
 *
 * Lo de "pronto" es a propósito: la alternativa era esconder lo que falta, y
 * entonces el menú miente por omisión — nadie sabe qué va a haber ni por qué.
 * Mostrarlo apagado dice dos cosas de una: este es el plan, y esta parte
 * todavía no.
 *
 * `alias` es cómo se la busca además de por su nombre (por ejemplo, cómo se
 * llamaba antes). Lo miran los dos buscadores de pantallas: el casillero del
 * menú, que NO lee el `sub`, y el de arriba. Sirve para encontrarla, no para
 * el orden. Hoy lo tiene sólo «Talleres y servicios», que hasta el 20/10 se
 * llamó «Proveedores».
 *
 * VER Y EDITAR SON DOS PERMISOS. `permiso` es lo que hace falta para ABRIR la
 * pantalla; `permisoEditar` es lo que hace falta para CAMBIAR algo adentro.
 * Sin el segundo la pantalla se abre igual, en sólo lectura. Es lo que permite
 * que tráfico mire el taller sin poder tocarlo, que era imposible cuando ver y
 * poder eran la misma cosa.
 *
 * El menú se arma con lo que la persona tiene, así que nadie ve una sección que
 * no puede abrir — pero eso es comodidad, no seguridad: quien decide de verdad
 * es la base, que verifica en cada llamada.
 */

export const FLOTABOT = "https://flotacantarini.fibot.ar";

/* Quién ve las pólizas y quién ve los siniestros (el dueño, 23/10): la llave
 * propia, o Satelital por default. Son las mismas reglas que hace valer la
 * base (políticas de `polizas_seguro` y `siniestros`, migración
 * 20261025150000) y FlotaBot en `/siniestros`: si una cambia, cambian las
 * tres. `PERMISO_SINIESTROS` no está en una sola entrada del menú —se reparte
 * entre Satelital y Seguros, ver los comentarios de cada una— y el test fija
 * que entre las dos cubran exactamente esto. */
export const PERMISO_POLIZAS = ["ver.polizas", "cargar.satelital"];
export const PERMISO_SINIESTROS = ["ver.siniestros", "cargar.satelital"];

export const SECCIONES = [
  {
    universo: "Novedades",
    grupo: "Novedades",
    items: [
      /* Sin permiso, y no por descuido: las tarjetas ya se filtran una por
         una contra lo que cada uno puede abrir (ver `services/novedades`), así
         que la pantalla nunca muestra una novedad de algo que no se puede
         mirar. Pedirle además un permiso a la pantalla dejaría a alguien sin
         sus propios tickets y sin sus propios mensajes. */
      /* ES LA HOME (`/`) y no una pantalla más. Antes `/` era un saludo con
         la hora —una pantalla entera para decir "buenas tardes"— y las
         novedades vivían aparte, o sea que había que ir a buscarlas. La
         primera pantalla del día tiene que decir qué hay que hacer hoy; si hay
         que navegar hasta ella, no se mira. */
      { id: "novedades", label: "Inicio", donde: "aca", ruta: "/", sub: "Qué pasó y qué hay que hacer" },
      // Sin permiso: cualquiera puede pedir algo y seguir lo suyo. El número
      // es lo que le toca a cada uno sin resolver, no cuántos hay en total —
      // un total ahí no le dice nada a nadie.
      { id: "tickets", label: "Tickets", donde: "aca", ruta: "/tickets", cuenta: "tickets", ocultarEnCero: true, sub: "Pedidos y problemas" },
      // Mensajes internos, no correo: el correo corporativo es GroupWise y no
      // se deja embeber. Esto resuelve otra cosa —la pregunta corta que hoy se
      // hace por el interno del teléfono— y para eso el correo nunca sirvió.
      // LOS `/ticket` DEL BOT, que no se veían en ningún lado del hub: quedaban
      // en `pedidos` + un issue de GitHub y sólo se miraban escribiendo a mano
      // una URL de FlotaBot. «Tickets» de acá arriba es la ticketera GENERAL,
      // por áreas (`routes/Tickets`), y ésta muestra sólo el área «sistema» —
      // tres cosas se llaman ticket (ver `services/pedidosSistema`) y la del
      // TALLER, las reparaciones, vive en FlotaBot.
      { id: "pedidos", corto: "Pedidos", label: "Pedidos al sistema", donde: "aca", ruta: "/pedidos", sub: "Lo que se pide con /ticket" },
      { id: "mensajes", label: "Mensajes", donde: "aca", ruta: "/mensajes", cuenta: "mensajes", ocultarEnCero: true, sub: "Escribirle a un compañero" },
      { id: "correo", label: "Correo", donde: "pronto" },
    ],
  },

  {
    /* EL SECTOR DE LAS GUÍAS (Igna, 06/10/2026): «un sector que sea "¿Cómo se
     * usa?" y ahí vamos a meter TODOS los HowTo que hicimos. Y de ahora en más
     * cada vez que se cree un HowTo, va para ese sector».
     *
     * Las guías no se listan acá: viven en FlotaBot (`/como/<slug>`) y la
     * pantalla le pregunta cuáles le tocan a quien mira (`/como/sector`), que
     * las recorta por permiso del lado del servidor. Una guía nueva aparece
     * sola, sin tocar el hub.
     *
     * Los dos ítems van sin permiso, a propósito: es el sector que explica el
     * sistema, y lo que cada uno ve adentro ya viene recortado. */
    universo: "¿Cómo se usa?",
    grupo: "¿Cómo se usa?",
    items: [
      { id: "guias", label: "Guías", donde: "aca", ruta: "/como-se-usa", sub: "Paso a paso, las de tu sector" },
      { id: "ayuda", label: "Primeros pasos", donde: "aca", ruta: "/ayuda", sub: "Entrar, la clave y cómo está armado el hub" },
    ],
  },

  {
    universo: "Tráfico",
    grupo: "Operación",
    items: [
      // EL MAPA NO LLEVA PERMISO, y es un cambio a propósito. Es la pregunta
      // que hace todo el mundo —¿dónde está el camión?— y la única respuesta
      // que no compromete nada: no deja cargar, ni corregir, ni exportar. Que
      // el de pañol tenga que pedir un permiso para ver dónde está una unidad
      // es fricción sin nada del otro lado.
      { id: "mapa", corto: "Mapa", sub: "Toda la flota, LYNX + SCANIA", label: "Mapa en vivo", donde: "flotabot", ruta: "/mapa" },
      { id: "reporte", permiso: "ver.trafico", sub: "Estado, chasis, consumo y último chofer", label: "Reporte", donde: "flotabot", ruta: "/reporte" },
      // Tractores afectados al régimen de Tierra del Fuego (padrón de Aldo)
      // cruzado con la posición en vivo: quién está en la Isla y quién cruzó al
      // continente. Lo miran tráfico y satelital.
      { id: "afectados", corto: "Afectados", permiso: ["ver.trafico", "cargar.trafico", "cargar.satelital"], sub: "Afectados a T. del Fuego: quién salió de la Isla", label: "Afectados a la Isla", donde: "flotabot", ruta: "/afectados" },
      // Viajes activos con ETA y % recorrido: los tramos abiertos de movimientos
      // diarios cruzados con la posición en vivo. El ETA sale de la duración
      // típica de cada corredor (histórico). Lo miran tráfico y satelital.
      { id: "en-ruta", corto: "En ruta", permiso: ["ver.trafico", "cargar.trafico", "cargar.satelital"], sub: "Viajes activos: ETA, % recorrido y estado en vivo", label: "En ruta", donde: "flotabot", ruta: "/en-ruta" },
      // Salidas y arribos de Ruta 3: cada unidad en viaje ubicada sola sobre la
      // ruta desde el GPS (el pueblo más cercano, parado/pasando/entre). Es la
      // planilla que arma Leonel a mano, automatizada. Beta: se coteja con
      // satelital. Imprimible. Lo miran tráfico y satelital.
      { id: "salidas-arribos", corto: "Ruta 3", permiso: ["ver.trafico", "cargar.trafico", "cargar.satelital"], sub: "Salidas y arribos: dónde está cada unidad en Ruta 3 (Beta)", label: "Salidas y arribos", donde: "flotabot", ruta: "/ruta3" },
      // Cuánto tarda un camión —y un chofer— en quedar liberado (listo para la
      // próxima carga) después de llegar: el hueco entre que un viaje llega y
      // sale el siguiente del mismo tractor/chofer (ticket #97, Gula). Sale de
      // movimientos; lo miran tráfico y satelital.
      { id: "liberacion", corto: "Liberación", permiso: ["ver.trafico", "cargar.trafico", "cargar.satelital"], sub: "Cuánto tarda en volver a salir un camión y un chofer", label: "Liberación", donde: "flotabot", ruta: "/liberacion" },
      // LA PREVISIÓN VA ARRIBA DE MOVIMIENTOS, y el orden es el dato: es el
      // paso ANTERIOR. Primero se planifica el día y después eso se pasa a
      // movimientos con un botón. Debajo, el menú contaría la operación al
      // revés de como se hace, y la pantalla nueva se leería como un anexo de
      // la vieja en vez de como el lugar donde se arranca.
      //
      // Cuelga de las MISMAS llaves que movimientos: es la misma persona
      // haciendo el mismo trabajo un paso antes. Con permisos propios habría
      // que acordarse de dar los dos, y el día que falte uno la pantalla se
      // abre y no deja cargar sin decir cuál falta.
      { id: "prevision", corto: "Previsión", permiso: "ver.trafico", permisoEditar: "cargar.trafico", sub: "Se planifica acá y se pasa a movimientos", label: "Previsión de carga", donde: "flotabot", ruta: "/prevision" },
      // "Movimientos diarios" y no "Movimiento de unidades": es como se llama
      // el archivo que esto reemplaza y como lo pide la gente.
      { id: "movimientos", corto: "Viajes", permiso: "ver.trafico", permisoEditar: "cargar.trafico", sub: "Los viajes, tramo por tramo", label: "Movimientos diarios", donde: "flotabot", ruta: "/movimientos" },
      // SUBIR LA PLANILLA. Nico, 30/08: Ricardo se niega a cargar los viajes
      // acá y los va a seguir anotando en el Excel; Aldo se compromete a
      // pasarlos al sistema. Esto es lo que hace que le cueste dos clicks.
      //
      // Cuelga de las mismas llaves que movimientos —es la misma persona
      // haciendo el mismo trabajo por otro camino— y pide `cargar.trafico`
      // para verlo, no sólo `ver.trafico`: la pantalla no sirve para mirar,
      // sirve para cargar, y ofrecérsela a quien no puede es avisar de una
      // puerta y esconder la llave.
      // NO LLEVA `permisoEditar`, y no es un olvido: acá ABRIR ES CARGAR.
      // No hay una versión de sólo lectura de esta pantalla —no hay nada que
      // mirar hasta que subís un archivo— así que `cargar.trafico` es la llave
      // para verla. Declarar el mismo permiso en los dos campos diría que
      // existe un nivel de lectura que no existe.
      { id: "subir-planilla", corto: "Subir planilla", permiso: "cargar.trafico", sub: "El Excel de movimientos, a los viajes del sistema", label: "Subir planilla de movimientos", donde: "aca", ruta: "/subir-planilla" },
      // El OTRO Excel: la planilla diaria de cargas (192 pestañas), que el
      // 31/08 intentaron subir por la pantalla de arriba y no era. FlotaBot
      // usa la pestaña más nueva y la manda a Previsión — de donde además
      // sale sola la ART (#122). Misma llave que la de movimientos: es la
      // misma gente cargando la misma área.
      { id: "subir-cargas", corto: "Cargas del día", permiso: "cargar.trafico", sub: "La planilla diaria de cargas, a la Previsión", label: "Subir cargas del día", donde: "aca", ruta: "/subir-cargas" },
      // «¿PUEDEN SALIR?» (Igna, 13/10: «que en la parte tráfico haya un lugar
      // donde si arrastras la lista de chóferes o pones el nombre, cepi les
      // pregunte si pueden salir de viaje xxxx día»). Se suelta la lista o se
      // escriben los nombres, se elige el día y Cepi les pregunta por
      // WhatsApp; las respuestas aparecen ahí mismo. Va con la operación del
      // día, abajo de las cargas: es el paso de saber quién sale.
      //
      // La abren Tráfico y Satelital (Nico entre ellos), con cualquiera de
      // las dos llaves de cargar. SIN `permisoEditar`: abrirla es para
      // preguntar, no hay una versión de sólo mirar. La pantalla es de
      // FlotaBot (las respuestas y los teléfonos viven allá, cerrados al
      // navegador) y la llave la vuelve a mirar FlotaBot en cada envío.
      { id: "pueden-salir", corto: "¿Salen?", permiso: ["cargar.trafico", "cargar.satelital"], sub: "Soltás la lista de choferes, elegís el día y Cepi les pregunta por WhatsApp", label: "¿Pueden salir?", donde: "flotabot", ruta: "/pueden-salir" },
      // Cómo recibió cada chofer su unidad — el estado al agarrarla, que
      // deslinda de qué se hace cargo. ANTES estaba mezclada en las fotos de
      // Taller, y a taller no le interesa cómo la recibieron: es de RRHH/tráfico.
      { id: "recepciones", corto: "Recepción", permiso: ["ver.trafico", "cargar.trafico", "ver.rrhh", "cargar.rrhh"], sub: "En qué estado recibió o dejó cada chofer su unidad (deslinde)", label: "Recepción de unidades", donde: "flotabot", ruta: "/recepciones" },
      // La cuenta corriente de cada chofer (Nico, 07/10): anticipos de la hoja
      // de ruta, gastos rendidos y lo que se anota a mano —adelantos,
      // descuentos, pagos—, con el saldo de cada uno. VA EN TRÁFICO Y NO EN
      // TCC (Igna, 08/10): es de los choferes de las cuatro empresas, no sólo
      // del circuito de Bautista. Las llaves no cambian: las mismas que pide
      // la página del bot (`cuenta_chofer`).
      { id: "cuenta_corriente_chofer", corto: "Cta. cte.", permiso: ["cargar.viajes", "cargar.rrhh", "ver.rrhh"], permisoEditar: ["cargar.viajes", "cargar.rrhh"], sub: "Anticipos, gastos rendidos, adelantos y descuentos de cada chofer, con su saldo", label: "Cuenta corriente de choferes", donde: "flotabot", ruta: "/cuenta-corriente" },
      // LA NOCHE, CAMIÓN POR CAMIÓN, Y EL BOTÓN DE AUTORIZAR (Igna, 09/10: «Lo
      // de Gabriel, sumá el "autorizado por"»). Gabriel autoriza al que sale
      // de noche con permiso y NO TIENE `ver.direccion`: por eso esto vive en
      // Tráfico y no al lado de «Infracción nocturna», en Dirección, que él
      // no ve. Lo abren las dos llaves —la de autorizar y la de los que ya
      // miran el anual— y los botones aparecen sólo con la primera: la
      // pantalla es de FlotaBot (la tabla de las noches está cerrada al
      // navegador) y la llave la vuelve a mirar FlotaBot en cada click.
      //
      // SATELITAL TAMBIÉN LA ABRE (Lautaro, 14/10, ticket #404): anota quién
      // manejaba cada noche y el seguimiento —lo que llevaba en su «BLACK
      // LIST»—. Eso no saca ni suma ninguna noche, así que alcanza con
      // `cargar.satelital`; `permisoEditar` sigue siendo la de autorizar, que
      // es lo único que cambia la cuenta. Qué puede escribir cada uno lo
      // decide FlotaBot.
      { id: "nocturnos-noche", corto: "Nocturnos", permiso: ["autorizar.nocturnos", "ver.direccion", "cargar.satelital"], permisoEditar: "autorizar.nocturnos", label: "Circulación nocturna", donde: "flotabot", ruta: "/nocturnos/noche", sub: "Quién manejaba cada noche, el seguimiento, y autorizar a quien salió con permiso" },
      // «Entrega de elementos» —el otro extremo de Recepción— SE MUDÓ A TALLER
      // el 14/09. Estaba acá porque se creyó que la hacía tráfico; Nico
      // corrigió que la hace el taller, que es donde están los elementos.
      // Tráfico la sigue viendo: el permiso la nombra explícitamente. Desde
      // el 05/10 (P-311) es parte de «Herramientas y elementos».
    ],
  },
  {
    universo: "Tráfico",
    grupo: "Satelital",
    items: [
      // UN SECTOR, UNA LLAVE. Antes eran dos —`ver.satelital` para mirar y
      // `cargar.satelital` para dibujar— y la separación existía para cinco
      // personas: 16 tenían la de ver y 11 la de editar.
      //
      // Lo que esas cinco ganaban era mirar unas zonas dibujadas sobre un
      // mapa, y las zonas no le dicen nada al que no las mantiene. El dato
      // que sí le interesa a todo el mundo —dónde está cada camión— vive en
      // el mapa, que no lleva permiso y lo abre cualquiera.
      //
      // Un permiso que separa dos cosas que nadie necesita separadas es una
      // llave más en la pantalla de permisos y una decisión más que tomar
      // cada vez que entra alguien.
      //
      // SIN `permisoEditar`, y eso también es la decisión: acá el que abre es
      // el que edita. Repetir la misma clave en los dos campos dejaría la
      // pantalla en sólo lectura para nadie — un modo que no le toca a
      // ninguna persona real es un modo que nunca se prueba.
      { id: "satelital", permiso: "cargar.satelital", label: "Satelital", donde: "aca", ruta: "/satelital" },
      { id: "geofences", permiso: "cargar.satelital", sub: "Zonas de entrada y salida", label: "Geocercas", donde: "flotabot", ruta: "/geofences" },
      // «Hacer el recorrido» (Igna, 06/10): interno + fechas, y el mapa con por
      // dónde anduvo, dónde paró y un play. El histórico de posiciones se venía
      // guardando desde el 05/09 y casi no se miraba. Lo sirve FlotaBot, que es
      // quien tiene el GPS; lo miran tráfico y satelital.
      { id: "recorrido", permiso: ["ver.trafico", "cargar.trafico", "cargar.satelital"], sub: "Por dónde anduvo un camión y dónde paró", label: "Recorrido", donde: "flotabot", ruta: "/recorrido" },
      // P-180 (Leonel): la planilla de arribos y cortes que Satelital armaba a
      // mano en el Excel del disco, ahora en el Hub. La parte viva (posición,
      // chofer, furgón…) sale sola del GPS y movimientos; Satelital sólo carga
      // la custodia y los cortes, y se guarda. Vive en flotabot. Beta: se está
      // afinando con ellos, como /ruta3.
      { id: "satelital_planilla", corto: "Planilla", permiso: "cargar.satelital", sub: "Arribos y cortes: la parte viva sale sola, vos cargás custodia y cortes", label: "Planilla de arribos y cortes", donde: "flotabot", ruta: "/satelital/planilla" },
      // Ticket #386 (Lautaro, 13/10): el historial de enganches de bitren en
      // Río Gallegos —qué chofer enganchó cada 2da parte, cliente y fechas—,
      // que Satelital llevaba en un Excel. Es de ELLOS y no sale de
      // Movimientos: «que no dependa de tráfico, sino de satelital, porque si
      // no la de tráfico está incompleta o desactualizada». La sirve FlotaBot
      // (tabla cerrada al navegador), con las columnas de su Excel y el Excel
      // para bajar. La mira tráfico, en sólo lectura; carga Satelital
      // (Lautaro, 19/10: «QUE SOLO PUEDAN VER LOS ENGANCHES Y FILTRAR NADA
      // MAS, PARA QUE NO MODIFIQUEN NADA»). `cargar.trafico` abre la pantalla
      // pero no carga: es la de Movimientos diarios, y la tiene casi todo
      // Tráfico. Lo decide FlotaBot en cada ruta (`_enganches_gate`).
      // Lautaro, 14/10: «necesitaría de ambos choferes». Cada enganche lleva
      // las DOS partes: la 1ra, la del titular, y la 2da, la del chofer que la
      // cruza entre Gallegos y Río Grande.
      { id: "satelital-enganches", corto: "Enganches", permiso: ["ver.trafico", "cargar.trafico", "cargar.satelital"], permisoEditar: "cargar.satelital", sub: "Bitrenes enganchados en Río Gallegos: las dos partes con su chofer, cliente y fechas", label: "Enganches de bitren", donde: "flotabot", ruta: "/satelital/enganches" },
      // P-190 (Leonel, 23/09): subir un Excel de internos y bajarlo con la
      // ubicación actual de cada uno. Nace porque la planilla de arribos no lo
      // deja imprimir el listado entero, y porque hoy va unidad por unidad. La
      // posición sale de la misma fuente que el mapa; la resuelve FlotaBot.
      { id: "satelital-posiciones", corto: "Ubicaciones", permiso: "cargar.satelital", sub: "Subís un Excel de internos y lo bajás con la ubicación actual de cada uno", label: "Ubicaciones por Excel", donde: "aca", ruta: "/satelital/posiciones" },
      // Arribos y Salidas (Leonel, #267): la misma herramienta que Ubicaciones
      // pero con su propia entrada, para llevar cada lista aparte sin pisar la
      // de ubicaciones. Mismo permiso y mismo endpoint.
      { id: "satelital-arribos", corto: "Arribos", permiso: "cargar.satelital", sub: "Subís tu lista de arribos y la bajás con la ubicación actual de cada interno", label: "Arribos", donde: "aca", ruta: "/satelital/arribos" },
      { id: "satelital-salidas", corto: "Salidas", permiso: "cargar.satelital", sub: "Subís tu lista de salidas y la bajás con la ubicación actual de cada interno", label: "Salidas", donde: "aca", ruta: "/satelital/salidas" },
      // Ticket #421 (Leonel, 15/10): «como el Excel que subo con posiciones
      // pero de datos que tenés de la empresa». Sube su Excel con SEMI y
      // CARGA, y lo baja con PAT SEMI, PAT CAMION, CHOFER, DNI, TEL y MARCA
      // completados. Lo resuelve FlotaBot (/api/satelital/completar).
      { id: "satelital-completar", corto: "Completar", permiso: "cargar.satelital", sub: "Subís un Excel con huecos y lo bajás completo con patentes, chofer, DNI y teléfono", label: "Completar datos por Excel", donde: "aca", ruta: "/satelital/completar" },
      // Ticket #432 (Lautaro, 16/10): traer del mail (o arrastrar) las
      // Torrecillas de Río Grande y que salgan su SALIDAS Y CUST, los bitrenes
      // en ARRIBOS BITREN de su ONLY BITRENES y los enganches cargados
      // (FlotaBot, /api/satelital/torrecillas/enganches).
      { id: "satelital-torrecillas", corto: "Torrecilla", permiso: "cargar.satelital", sub: "Traés o arrastrás las Torrecillas y salen SALIDAS Y CUST, ONLY BITRENES y los enganches", label: "Torrecillas", donde: "aca", ruta: "/satelital/torrecillas" },
      // Ticket 431 (Lautaro, 16/10): cuando el chofer en ruta no tiene la
      // tarjeta YPF, no tiene cupo o se le rompió, Satelital le pasa por correo
      // una orden de gas oil a una YPF con cuenta corriente. Como el correo de
      // los enganches: se elige el chofer y trae sus datos y su tractor, y lo
      // manda el que lo genera desde su correo. Queda en `ordenes_gasoil`.
      { id: "satelital-orden-gasoil", corto: "Gas oil", permiso: "cargar.satelital", sub: "Orden de gas oil para una YPF con cuenta corriente: elegís el chofer y sale el correo", label: "Orden de gas oil", donde: "aca", ruta: "/satelital/orden-gasoil" },
      // Los choferes habilitados a usar el bot. Lo mantiene Satelital, que tiene
      // los números al día: nombre/legajo/empresa salen solos de RRHH, ellos
      // ponen número y tractor. Es de ellos porque son los que saben.
      { id: "choferes-autorizados", corto: "Choferes", permiso: ["ver.choferes", "cargar.choferes"], permisoEditar: "cargar.choferes", sub: "Los choferes habilitados a usar el bot: número y tractor asignado", label: "Choferes autorizados", donde: "aca", ruta: "/choferes-autorizados" },
      // Lo que los chóferes avisan del camino por WhatsApp (cortes, clima,
      // demoras): la planilla /avisos del bot, tal cual, con Excel. Antes sólo
      // se llegaba por el WhatsApp que les llega en el momento o tipeando la URL.
      { id: "avisos-ruta", corto: "Avisos", permiso: ["ver.trafico", "cargar.trafico", "cargar.satelital"], sub: "Lo que los chóferes avisan del camino, tal cual", label: "Avisos de ruta", donde: "flotabot", ruta: "/avisos" },
      // Los choques que reportan los chóferes con /siniestro o /choque: foto,
      // datos y el punto del GPS del camión a esa hora, en el mapa.
      //
      // Desde el 23/10 la llave es `ver.siniestros`, y Satelital los ve por
      // default (el dueño: «los tiene que poder ver satelital por default»).
      // ACÁ, en el grupo de Satelital, va sólo `cargar.satelital`; quien
      // tiene `ver.siniestros` la ve en el universo Seguros. Así a nadie le
      // aparece un universo que no tenía por tener una sola llave, y entre
      // las dos entradas cubren exactamente lo que pide FlotaBot para abrir
      // la página (`ver.siniestros` o `cargar.satelital`). Ver
      // `PERMISO_SINIESTROS` y `secciones.seguros.test.js`.
      { id: "siniestros", corto: "Siniestros", permiso: "cargar.satelital", sub: "Choques que reportan los chóferes, con la ubicación del camión en el mapa", label: "Siniestros", donde: "flotabot", ruta: "/siniestros" },
    ],
  },
  {
    universo: "Tráfico",
    grupo: "Combustible",
    items: [
      { id: "consumo", corto: "Consumo", permiso: "ver.combustible", sub: "Estado, AdBlue, tacógrafo y hace cuánto no se mueve", label: "Consumo Scania", donde: "flotabot", ruta: "/scaniareport" },
      // CCyU — control de combustible y urea. Reemplaza a la pantalla que se
      // llamaba "Cargas de gasoil" y NO CARGABA NADA: era una consulta de
      // totales contra Scania, sobre una tabla de 19 filas (una con la fecha
      // rota). Acá el consumo sale del histórico diario propio y la urea de
      // los vales, así que las dos cosas se ven en el mismo gráfico.
      { id: "ccyu", permiso: "ver.combustible", sub: "Gasoil día por día, con la urea encima", label: "CCyU", donde: "flotabot", ruta: "/ccyu" },
      // Viajes va con combustible y no con operación: lo mira el que reparte
      // el gasoil, que es quien necesita saber qué viaje viene.
      { id: "viajes", permiso: "ver.combustible", permisoEditar: "cargar.combustible", label: "Viajes", donde: "aca", ruta: "/viajes", cuenta: "viajes" },
      // P-93 (Gula): qué se corrigió de un viaje —destino, lugar de carga,
      // cliente, fechas—, de qué a qué, quién y cuándo. Lo mira Control de
      // Combustible y Rendición para rehacer la cuenta de km cuando la ruta
      // cambió. Vive en flotabot; lo ven también tráfico y RRHH.
      { id: "movimientos_cambios", corto: "Cambios", permiso: ["ver.combustible", "ver.trafico", "ver.rrhh"], sub: "Qué cambió de cada viaje (destino, carga, fechas), de qué a qué y quién", label: "Cambios de viajes", donde: "flotabot", ruta: "/movimientos/cambios" },
    ],
  },
  /* UREA / ADBLUE, GRUPO PROPIO Y NO UN RINCÓN DE COMBUSTIBLE.
   *
   * Igna, 18/09: «necesito que haya en "Permisos" un sector aparte para poder
   * ver en el hub todo lo relacionado a la Urea/Adblue = "Vales del surtidor,
   * Cargas de Urea, y lectura de vales"». La llave nueva es `ver.urea`, y el
   * grupo existe por lo que pasaba sin él: dárselo a alguien le dibujaba dos
   * ítems bajo un título que decía «Combustible», un sector que esa persona no
   * tiene. Un menú que nombra mal lo que muestra manda a pedir el permiso
   * equivocado.
   *
   * LAS TRES LLEVAN LAS DOS LLAVES (`ver.combustible` y `ver.urea`), y el
   * `puedeVer` es cualquiera-de: el que hoy ve los vales por combustible los
   * sigue viendo igual. Lo que se gana es poder dar SÓLO urea —Jorge, Silvana—
   * sin entregar el gasoil, Consumo Scania, CCyU y Viajes de arriba.
   *
   * `ver.lecturas` se queda como la llave de Lecturas y NO se reemplaza por
   * `ver.urea`: esa pantalla no mira, CORRIGE (es un UPDATE sobre la carga), y
   * juntarlas convertiría «ver la urea» en «poder cambiar lo que dice el
   * vale». Por eso va tercera y con su llave propia, en el mismo grupo.
   */
  {
    universo: "Tráfico",
    grupo: "Urea / AdBlue",
    items: [
      // El parte del surtidor de UREA. Decía «Urea y gasoil» porque se
      // pensaba que el vale de gasoil era el mismo talonario con otra palabra
      // en DESCRIPCIÓN; el primer vale de gasoil real (Nico, 07/10) trajo su
      // propio talonario —Nº 283280 contra el 9552 de la urea— y tiene su
      // grupo propio, abajo: «Gasoil de la base».
      { id: "vales", corto: "Vales", permiso: ["ver.combustible", "ver.urea"], sub: "Urea del surtidor, con la foto del vale", label: "Vales del surtidor", donde: "flotabot", ruta: "/urea" },
      // El histórico NATIVO del hub, al lado del panel del bot y no en vez
      // de él: éste elige el período con calendario y —sólo `admin.borrar`—
      // saca la carga duplicada, que el panel del bot no puede (la pared es
      // la RLS del hub, no la sesión del bot).
      { id: "ureacargas", corto: "Cargas", permiso: ["ver.combustible", "ver.urea"], sub: "El histórico por calendario; borrar la mal cargada", label: "Cargas de urea", donde: "aca", ruta: "/urea/historico" },
      /* La auditoría del OCR de vales. Estaba en «Sistema», que es donde se
       * mira el bot y no donde se mira la urea: el que revisa una lectura
       * viene de ver el vale, no de ver los accesos al hub.
       *
       * Permiso propio (`ver.lecturas`): el que saca la foto no puede juzgar
       * la lectura, y el que puede no veía la foto — acá están las dos cosas
       * juntas y el veredicto que se deja es el dataset del que salen las
       * reglas del prompt. */
      { id: "lecturas", corto: "Lecturas", permiso: "ver.lecturas", label: "Lecturas de vales", donde: "aca", ruta: "/lecturas", sub: "La foto contra lo que leyó el bot" },
    ],
  },
  /* GASOIL DE LA BASE, AL LADO DE LA UREA Y NO ADENTRO.
   *
   * Nico, 07/10: «un lugar como el de los vales de Urea pero para el gasoil
   * cargado en la base». Es otro talonario («GAS OIL», Nº 283280) que se
   * despacha del surtidor propio y se carga igual —foto del vale al bot—, así
   * que va pegado a Urea / AdBlue. Grupo propio porque el título de arriba
   * dice urea, y un ítem de gasoil abajo de ese título manda a pedir la llave
   * equivocada (la lección del 18/09).
   *
   * DOS LLAVES, CUALQUIERA DE LAS DOS: `ver.gasoil` (sólo esto) o
   * `ver.combustible` (el que ya ve el gasoil entero no lo pierde acá).
   */
  {
    universo: "Tráfico",
    grupo: "Gasoil de la base",
    items: [
      { id: "gasoilbase", corto: "Gasoil", permiso: ["ver.combustible", "ver.gasoil"], sub: "Vales de GAS OIL del surtidor propio: chofer, unidad, litros y totales", label: "Vales de gasoil", donde: "aca", ruta: "/gasoil" },
    ],
  },
  {
    universo: "Tráfico",
    grupo: "Documentación",
    items: [
      // Pasó del panel del bot al hub porque ahora ESCRIBE: hasta hoy los
      // vencimientos sólo se podían corregir abriendo el Excel o mandándole un
      // mensaje al bot, y no había ninguna pantalla donde cargar.
      { id: "documentos", permiso: "ver.documentos", permisoEditar: "cargar.documentos", sub: "Vencimientos de los choferes", label: "Documentos", donde: "aca", ruta: "/documentacion" },
      // La agenda de turnos. ESTUVO EN «pronto» DESPUÉS DE ESTAR HECHA: el
      // comentario decía «todavía no existe la tabla» y `rto_turnos` existe
      // desde hace rato, con su pantalla andando adentro del RTO. O sea que en
      // el menú figuraba apagada mientras funcionaba — el peor de los dos
      // mundos, porque el que la necesitaba leía «está en camino» y no la
      // buscaba, y el que la usaba llegaba sólo si sabía que era una solapa.
      //
      // Apunta al RTO con la solapa puesta y no a una ruta propia: la agenda
      // es una vista de esa pantalla, y darle dirección propia serían dos
      // lugares para lo mismo.
      { id: "agenda", permiso: "ver.documentos", label: "Agenda de turnos", donde: "aca", ruta: "/rto?vista=agenda", sub: "Turnos de RTO, registro + psicofísico y peligrosas" },
      // «Cargar datos» —antes «Recursos»— SALE DEL MENÚ. Era la pantalla desde
      // la que se daba de alta y se corregían los padrones, y ahora eso se
      // hace en el padrón mismo, con el lápiz. Dos lugares para cargar lo
      // mismo obligan a adivinar en cuál, y el día que se acierte el que no es
      // ese trabajo aparece en una lista que nadie mira — es lo mismo que pasó
      // con las dos pantallas de taller.
      //
      // La ruta `/recursos` sigue viva a propósito, por los enlaces que ya se
      // mandaron: lo que deja de tener es una puerta en el menú.
      // EL RTO YA NO SE DIBUJA EN EL MENÚ (`oculto: true`). Eran dos ítems —«RTO
      // de tractores» y «RTO de acoplados»— y cada uno era el padrón de esa
      // flota con una columna más: la del vencimiento. O sea dos puertas al
      // mismo listado, y encima al revés de lo esperable —el padrón mostraba los
      // datos y no abría nada, y el «RTO de …» sí abría la ficha—. Igna, 09/09:
      // «unificar todo, que sea únicamente Tractores y Furgones, que en el
      // pantallazo te diga si la RTO está al día y al hacer click te traiga la
      // ficha».
      //
      // Ahora el vencimiento es una COLUMNA del padrón (Tractores/Furgones), con
      // su semáforo y su filtro: se elige «Vencido» y queda la lista del lunes.
      // La fecha del título (los 0 km) se carga por unidad desde la ficha, que
      // ya lo hacía. La agenda de turnos sigue siendo su propio ítem, arriba.
      //
      // NO SE BORRAN DEL CATÁLOGO, sólo del rail: las pantallas siguen vivas como
      // destino de los avisos de la home («3 tractores vencidos» abre la lista
      // ordenada por urgencia) y del enlace «Ir al RTO» de la ficha, y son estos
      // ítems los que le dan a esos avisos su permiso (`puede("rto")`) y a esas
      // rutas su título. Quitar la entrada del catálogo apagaría las tarjetas de
      // la home sin que nadie lo pida. `oculto` saca del menú y deja todo lo
      // demás en pie — es exactamente lo que se pidió.
      { id: "rto", corto: "RTO", permiso: "ver.documentos", label: "RTO de tractores", donde: "aca", ruta: "/rto", oculto: true, cuenta: "rto", ocultarEnCero: true, sub: "Vencimientos ordenados por urgencia y agenda de turnos" },
      { id: "rto_furgones", corto: "RTO acopl.", permiso: "ver.documentos", label: "RTO de acoplados", donde: "aca", ruta: "/furgones-rto", oculto: true, sub: "Vencimientos de los acoplados" },
      // Mismo permiso que RTO: es la misma clase de pantalla (consulta a un
      // organismo, por dominio) y la misma gente la mira. La consulta en sí
      // se pide desde la ficha de cada unidad -- acá sólo se ve lo ya sabido.
      { id: "multas", corto: "Multas", permiso: "ver.documentos", label: "Multas de tránsito", donde: "aca", ruta: "/multas", sub: "PBA, CABA y SINAI por dominio" },
    ],
  },

  {
    /* TCC es su propio universo (Igna, 31/08: «quiero que todo esté en un
     * sector en especial que sea TCC»). Es el circuito de viajes que hace
     * Bautista de punta a punta —IDEAS.md §7 del bot— y va a crecer: hoja de
     * ruta, calendario, y más adelante carta de porte y rendición. Las
     * llaves: `ver.tcc` VE el sector (llave dedicada, Igna 08/09 — antes lo
     * abría `ver.trafico`, que lo tienen 20 y llenaba TCC de gente que no es de
     * acá), y `cargar.viajes` es Bautista, que ve y CARGA sin ser de tráfico ni
     * de RRHH. Editar/emitir sigue con `cargar.viajes` o `cargar.trafico`. */
    universo: "TCC",
    grupo: "TCC",
    items: [
      { id: "hoja_ruta", corto: "Hoja de ruta", permiso: ["ver.tcc", "cargar.viajes"], permisoEditar: ["cargar.viajes", "cargar.trafico"], sub: "El alta del viaje: se pre-llena de Movimientos e imprime los tres papeles", label: "Hoja de ruta", donde: "flotabot", ruta: "/hoja-de-ruta" },
      { id: "carta_porte", corto: "Carta de porte", permiso: ["ver.tcc", "cargar.viajes"], permisoEditar: ["cargar.viajes", "cargar.trafico"], sub: "Se pre-llena de la hoja de ruta y se imprime sobre el troquelado", label: "Carta de porte", donde: "flotabot", ruta: "/carta-de-porte" },
      { id: "rendicion", corto: "Rendición", permiso: ["ver.tcc", "cargar.viajes"], permisoEditar: ["cargar.viajes", "cargar.trafico"], sub: "Gastos con totales que se suman solos y km del GPS", label: "Rendición de viaje", donde: "flotabot", ruta: "/rendicion" },
      { id: "calendario_viajes", corto: "Calendario", permiso: ["ver.tcc", "cargar.viajes"], sub: "Días de viaje pintados y km del mes por chofer", label: "Calendario de viajes", donde: "flotabot", ruta: "/calendario-viajes" },
      // Lo que se le paga a cada fletero de Transcan (Bautista, 13/10):
      // anticipos, viajes liquidados y pagos, con su saldo. VA EN TCC (Igna,
      // 13/10: «en TCC va esta») y no al lado de la de choferes, en Tráfico:
      // es otra cuenta, con el signo al revés —acá positivo es lo que le
      // debemos—. Mirar lo abre `ver.tcc`; tocar, sólo `cargar.viajes`, que
      // es Bautista: es plata, así que `cargar.trafico` no alcanza.
      { id: "cuenta_fleteros", corto: "Fleteros", permiso: ["ver.tcc", "cargar.viajes"], permisoEditar: ["cargar.viajes"], sub: "Anticipos, viajes liquidados y pagos de cada fletero, con su saldo", label: "Cuenta corriente de fleteros", donde: "flotabot", ruta: "/cuenta-fleteros" },
    ],
  },

  {
    universo: "Portería",
    grupo: "Portería",
    items: [
      // "Ingreso y egreso" y no "Portería": el universo ya se llama así, y un
      // título "PORTERÍA" con un ítem "Portería" pegado abajo se lee como un
      // error de la pantalla. Además dice lo que se hace adentro, que es lo
      // que el nombre viejo no decía.
      { id: "porteria", corto: "Barrera", permiso: "ver.porteria", permisoEditar: "cargar.porteria", label: "Ingreso y egreso", donde: "aca", ruta: "/porteria", cuenta: "eventos", sub: "Vehículos y personas en la barrera" },
      // Quién está adentro AHORA. `eventos` dice qué pasó y no en qué estado
      // quedó cada unidad: había 6.898 entradas y 6.951 salidas y nadie las
      // cruzaba, así que la única forma de saber quién estaba en el playón era
      // mirar por la ventana de portería.
      { id: "playon", corto: "Playón", permiso: "ver.porteria", label: "El playón", donde: "flotabot", ruta: "/playon", sub: "Quién está adentro ahora" },
      /* Las visitas cuelgan de `ver.porteria` y NO de una llave nueva: es el
       * registro del portón y vive en su universo, así que quien puede abrir
       * «Ingreso y egreso» puede abrir esto. Un permiso propio sería una llave
       * que no abre nada distinto — y una que hay que acordarse de dar es una
       * que un día no se da, y ahí la pantalla «no anda» sin nada que lo
       * explique. */
      { id: "visitas", corto: "Visitas", permiso: "ver.porteria", label: "Visitas", donde: "aca", ruta: "/visitas", sub: "Quién entró caminando y a quién vino a ver" },
      /* Vigilancia lleva PERMISO PROPIO (`ver.vigilancia`), no `ver.porteria`:
       * es otra gente la que autoriza y otra la que registra, y no queremos que
       * abrir la barrera de unidades implique ver la whitelist del portón ni al
       * revés. Vive en este universo porque es el mismo portón físico, pero la
       * llave la decide Nico persona por persona. */
      { id: "vigilancia", corto: "Autos", permiso: ["ver.vigilancia", "cargar.vigilancia", "autorizar.vigilancia"], permisoEditar: ["cargar.vigilancia", "autorizar.vigilancia"], label: "Autos Autorizados", donde: "aca", ruta: "/vigilancia", sub: "Autorizados a entrar y registro de accesos del portón" },
      /* «EVENTOS» SALIÓ DEL MENÚ: era la MISMA tabla que «Ingreso y egreso».
       * Una es la pantalla donde se carga y la otra era `eventos` en crudo —
       * las dos sobre `public.eventos`, y las dos con `cuenta: "eventos"`, que
       * era la pista a la vista. Dos entradas para lo mismo obligan a adivinar
       * en cuál se mira, y el día que se acierte la que no es, alguien
       * concluye que un movimiento no se cargó. Es la lección de los dos
       * talleres.
       *
       * La ruta `/padron/eventos` sigue viva, como `/recursos`: lo que deja de
       * tener es una puerta en el menú. Y los datos no se tocan. */
      /* La llave se entrega en la barrera, así que la carga es de portería.
       * Desde el 27/10 (ticket 522, Nico) tiene pantalla: quién tiene cada
       * llave ahora, el QR de cada una para imprimir, y `/llaves/:interno`,
       * a donde lleva escanearlo, para anotar el retiro o la vuelta. */
      { id: "llaves", corto: "Llaves", permiso: "ver.porteria", label: "Entrega de llaves", donde: "aca", ruta: "/llaves", sub: "Quién tiene cada llave y desde cuándo", alias: "llave qr" },
    ],
  },

  {
    universo: "Taller",
    grupo: "Taller",
    items: [
      // ACÁ HABÍA DOS PANTALLAS MÁS —"Taller tractores" y "Taller furgones"—
      // heredadas de CantApp, y las reemplazó la ticketera. Escribían en
      // `eventos` con `area` tractores/furgones y **nadie las usaba desde
      // fines de junio**: 6.468 registros históricos y ni uno nuevo en siete
      // semanas. Dos pantallas que hacen lo mismo que una tercera obligan a
      // adivinar en cuál se carga, y el día que se cargue en la que no es, el
      // trabajo no aparece en ningún informe.
      //
      // Los registros viejos NO se borraron: siguen en `eventos`, con su
      // proveedor, remito y costo. Lo que no tienen es pantalla.
      //
      // El permiso es PROPIO y no `ver.taller`: el padrón del taller es el
      // archivo y la ticketera es el trabajo del día. A un mecánico se le
      // puede querer dar una sin la otra. FlotaBot lo hace cumplir de verdad
      // — antes esto sólo decidía si la baldosa se dibujaba.
      { id: "ticketera", corto: "Taller", permiso: "ver.ticketera", permisoEditar: "cargar.taller", label: "Ticketera de taller", donde: "flotabot", ruta: "/taller", sub: "Tickets de reparación, SLA y mecánicos" },
      // P-309 / P-310 (Nico, 05/10): lo que el chofer manda por WhatsApp con
      // «taller …» —lo que ÉL cree que hay que hacerle al camión—. Convive con
      // la hoja que dejan en el taller y la va a reemplazar. Andrés y Rincón
      // filtran (se hace / no se hace), asignan el mecánico y lo marcan hecho;
      // la base guarda quién y cuándo. Va pegada a la Ticketera porque es el
      // paso de ANTES: la ticketera es la visita, esto es el pedido.
      // `cargar.taller` también abre: la pantalla lo acepta, y quien puede
      // mover un pedido sin ver el ítem en el menú no lo encontraría.
      { id: "pedidos_choferes", corto: "Pedidos", permiso: ["ver.taller", "ver.ticketera", "cargar.taller", "cargar.panol"], permisoEditar: "cargar.taller", label: "Pedidos de chóferes", donde: "aca", ruta: "/taller/pedidos-choferes", sub: "Lo que los chóferes piden por WhatsApp que se le haga al camión" },
      // «Fotos de vehículos» (P-174) SE SACÓ del menú (Nico, ticket 575,
      // 30/10/2026): *«no está en uso y no hay info. Las fotos van a avisos en
      // ruta o a pedidos de chóferes»*. La pantalla de FlotaBot sólo mostraba
      // roturas y nunca se cargó ninguna; las 26 fotos de la tabla son
      // recepciones, que tienen su sector (`/recepciones`). La tabla
      // `fotos_vehiculo` y la ruta siguen: las usan recepciones y avisos.
      // El shadow board: qué herramienta está en la pared y cuál se la llevó
      // alguien. Vive ACÁ y no en Pañol porque no es stock que se consume: es
      // la pared del taller, y la mira el que la tiene enfrente.
      //
      // HERRAMIENTAS Y ENTREGA DE ELEMENTOS SON UNA SOLA (Nico, P-311, 05/10):
      // *«La sección herramienta y entrega de elementos deberían ser la misma
      // y verse como lo hace herramientas. Al fin y al cabo salen tmb del pañol
      // pero el destinatario es un chofer y no un mecánico (única
      // diferencia)»*. «Entrega de elementos» era una página de FlotaBot
      // (`/elementos`); sus actas pasaron a ser entregas del pañol y el chofer
      // tiene su caja en el mismo estante. Las direcciones viejas
      // (`/panel/elementos`, `/elementos`) caen en las cajas de los choferes.
      //
      // TRÁFICO NO LA PIERDE: Entrega de elementos se abría también con
      // `ver.trafico` (tiene la otra punta del deslinde, `/recepciones`), así
      // que esta pantalla se abre con cualquiera de las dos. Entregar sigue
      // pidiendo `cargar.taller`, que es la que saca algo del pañol.
      { id: "herramientas", corto: "Herram.", permiso: ["ver.taller", "ver.trafico"], permisoEditar: "cargar.taller", label: "Herramientas y elementos", donde: "aca", ruta: "/taller/herramientas", sub: "La caja de cada uno —mecánico o chofer— con su papel firmado, y lo que presta cada pañol" },
      // P (Nico, 22/09): los camiones en garantía van a Scania, que emite una
      // orden de trabajo; se sube la OT y queda registrado el trabajo con su
      // fecha, patente y chasis. Nativo del Hub («aca»): se carga y se consulta.
      { id: "garantias", corto: "Garantías", permiso: "ver.taller", permisoEditar: "cargar.taller", label: "Garantías", donde: "aca", ruta: "/taller/garantias", sub: "Trabajos de garantía en Scania: la orden de trabajo, qué se hizo y cuándo" },
      // El padrón de herramientas. Pide `cargar.taller` para ENTRAR y no sólo
      // para editar: una pantalla de altas y bajas que se abre en sólo lectura
      // no muestra nada que el tablero no muestre mejor. Por eso no declara
      // `permisoEditar`: acá ver y poder son lo mismo, y repetir el permiso
      // haría creer que hay dos niveles donde hay uno.
      { id: "herramientas_admin", corto: "Herram. ✎", permiso: "cargar.taller", label: "Herramientas · administrar", donde: "aca", ruta: "/taller/herramientas/administrar", sub: "Alta, baja y datos de cada herramienta" },
    ],
  },

  {
    /* ── PUERTO ────────────────────────────────────────────────────────
     *
     * Universo nuevo, y son dos pantallas de MIRAR. Lo que se carga entra por
     * WhatsApp: la gente de puerto ficha con la foto del molinete y anota los
     * trabajos desde el teléfono, con las manos sucias. Duplicar acá la carga
     * obligaría a adivinar en cuál de los dos lados se anota — es la lección de
     * los dos talleres.
     *
     * EL PERMISO ES PROPIO Y NO `ver.taller`. El fichaje son los HORARIOS de dos
     * personas concretas, así que se da uno por uno: mismo criterio que
     * `ver.direccion` y `ver.pin_ypf`, que están fuera de los atajos. Y lo que
     * de verdad restringe es la RLS, no esta línea.
     */
    universo: "Puerto",
    grupo: "Puerto",
    items: [
      { id: "puertofichaje", corto: "Fichaje", permiso: "ver.puerto", label: "Fichaje", donde: "aca", ruta: "/puerto/fichaje", sub: "Entradas y salidas, con la foto del molinete" },
      { id: "puertotrabajos", corto: "Trabajos", permiso: "ver.puerto", permisoEditar: "cargar.puerto", label: "Trabajos del puerto", donde: "aca", ruta: "/puerto/trabajos", sub: "Qué le hicieron a cada camión" },
      // P-181 (Natalia): inconvenientes del turno que el personal manda por
      // WhatsApp (`novedad ...`). Se ven con ver.puerto; anular con cargar.puerto.
      { id: "puertonovedades", corto: "Novedades", permiso: "ver.puerto", permisoEditar: "cargar.puerto", label: "Novedades del puerto", donde: "aca", ruta: "/puerto/novedades", sub: "Inconvenientes que carga el personal por WhatsApp" },
    ],
  },

  {
    universo: "Padrones",
    grupo: "Padrones",
    items: [
      { id: "tractores", permiso: "ver.flota", permisoEditar: "cargar.flota", label: "Tractores", donde: "aca", ruta: "/padron/tractores", cuenta: "tractores", sub: "Padrón de tractores" },
      { id: "furgones", permiso: "ver.flota", permisoEditar: "cargar.flota", label: "Furgones", donde: "aca", ruta: "/padron/furgones", cuenta: "furgones", sub: "Padrón de furgones" },
      // Oculto del menú (Nacho, 24/09): «no nos da nada importante». `oculto`
      // lo saca del rail pero deja la ruta viva, por si se quiere volver.
      { id: "vehiculos", permiso: "ver.flota", permisoEditar: "cargar.flota", label: "Vehículos", donde: "aca", ruta: "/padron/vehiculos", cuenta: "vehiculos", sub: "Livianos y utilitarios", oculto: true },
      /* DOS LLAVES, Y ALCANZA CON UNA. Pedido de Nico (25/08/2026): «que Erick
       * y Ale puedan dar de alta un empleado o de baja». Erick tiene
       * `cargar.rrhh` y no `cargar.gente` — con una sola llave acá, el padrón
       * se le abría en sólo lectura y el alta la terminaba pidiendo por
       * mensaje. Quien maneja legajos, partes de enfermo y suspensiones puede
       * corregir la fila de esa misma persona.
       *
       * No es sólo la pantalla: la RLS de `personas` pide el mismo par
       * (`cargar.gente OR cargar.rrhh`) para escribir. Si divergen, el lápiz
       * aparece y el guardado rebota — que es peor que no ver el lápiz. */
      { id: "personas", corto: "Personal", permiso: "ver.gente", permisoEditar: ["cargar.gente", "cargar.rrhh"], label: "Choferes y personal", donde: "aca", ruta: "/padron/personas", cuenta: "personas", sub: "Padrón de gente" },
      // Los 170 que cobran como chofer, contra las 2.476 filas del padrón de
      // arriba. No lleva `permisoEditar` porque no se edita: el origen es el
      // export de sueldos y una corrección acá duraría hasta la próxima
      // importación.
      { id: "choferes", corto: "Choferes", permiso: "ver.gente", label: "Choferes", donde: "aca", ruta: "/choferes", sub: "Los que están en la nómina de este mes" },
    ],
  },

  {
    /* ── RRHH ──────────────────────────────────────────────────────────
     *
     * Universo propio y no un grupo adentro de Padrones, por la misma razón
     * que Puerto: es un SECTOR de la empresa, con dos personas adentro —Vilte
     * y Erick—, y su llave no la tiene nadie más. Metido en Padrones, el rail
     * mostraría un sector que casi todos abren con dos ítems apagados abajo.
     *
     * «Choferes» se queda en Padrones a propósito, aunque salga de la misma
     * tabla: lo abre cualquiera con `ver.gente` y es la lista que usa tráfico
     * para armar un viaje. Traerlo acá lo escondería detrás de una llave que
     * tienen dos.
     *
     * Y no hay ítem de «propuestas»: las del buzón de mails se revisan con
     * `/rrhh` en WhatsApp. Igna lo dijo derecho —«no hace falta»— y una
     * pantalla más para lo mismo sería otro lugar donde mirar.
     */
    universo: "RRHH",
    grupo: "RRHH",
    items: [
      // Las 292 personas que cobran, con lo que el padrón no tiene: antigüedad
      // reconocida, encuadre del convenio, contrato y motivo de egreso. Sin
      // `permisoEditar` porque no se edita: el origen es el export de Erick y
      // una corrección acá duraría hasta la próxima importación.
      { id: "nomina", corto: "Nómina", permiso: "ver.rrhh", label: "Nómina", donde: "aca", ruta: "/rrhh/nomina", sub: "Los que cobran este mes, según el export de sueldos" },
      // Las 92 filas donde la nómina y el padrón no dicen lo mismo. Es la lista
      // de trabajo de RRHH, y ninguna se resuelve sola.
      { id: "diferencias", corto: "Difs.", permiso: "ver.rrhh", label: "Diferencias", donde: "aca", ruta: "/rrhh/diferencias", sub: "Dónde la nómina y el padrón no coinciden" },
      // Partes de enfermo, vacaciones y francos. Se mira con ver.rrhh y se carga con
      // cargar.rrhh (la pared es la RLS); alimenta las tarjetas de la ficha
      // y la enfermería y la laguna del patio del bot.
      { id: "licencias", corto: "Lic.", permiso: "ver.rrhh", label: "Licencias", donde: "aca", ruta: "/rrhh/licencias", sub: "Partes de enfermo, vacaciones y francos, cargados por RRHH" },
      // La hoja de viaje del mes, que reemplaza el PDF que RRHH imprimía y
      // pintaba a mano. VIVE EN FLOTABOT y no acá porque los días amarillos
      // salen de `movimientos`, que es una tabla del bot: traerla al hub
      // obligaría a abrirle esa tabla al navegador. Lo único que se escribe
      // es el número de viaje, y para eso pide `cargar.rrhh`.
      // `cargar.viajes` es LA LLAVE PROPIA de la hoja (Igna, 31/08):
      // eventualmente no la va a cargar RRHH, y darle `cargar.rrhh` al que
      // cargue le abriría la nómina entera. RRHH la sigue viendo y cargando
      // con sus llaves de siempre.
      { id: "hoja_viaje", corto: "Hoja", permiso: ["ver.rrhh", "cargar.viajes"], permisoEditar: ["cargar.rrhh", "cargar.viajes"], label: "Hoja de viaje", donde: "flotabot", ruta: "/hoja-de-viaje", sub: "Los días de viaje del mes, con el número de cada uno" },
      // La planilla de km del mes (CCT 40/89) con la que se liquida. Va al lado
      // de la Hoja de viaje —con aquélla se ve qué días viajó, con ésta se
      // arman los km— y vive en flotabot por lo mismo: los tramos salen de
      // `movimientos`. Se mira, no se escribe: sin `permisoEditar`.
      { id: "planilla_tramos", corto: "Tramos", permiso: ["ver.rrhh", "cargar.viajes"], label: "Planilla de tramos", donde: "flotabot", ruta: "/planilla-tramos", sub: "La planilla de km del mes (CCT 40/89), para liquidar" },
      // El calendario de viajes, TAMBIÉN acá (Nico, 08/09): nació en el sector
      // TCC —el papel de Bautista— y RRHH lo necesita para las cuatro empresas
      // (una solapa por TC/TCC/EX/TA). Es la MISMA página de flotabot que la de
      // TCC (`ruta` idéntica); cambia el `id` porque no puede repetirse y el
      // panel abre por id. La solapa por empresa vive adentro de la página.
      { id: "calendario_rrhh", corto: "Calend.", permiso: ["ver.rrhh", "cargar.viajes"], label: "Calendario de viajes", donde: "flotabot", ruta: "/calendario-viajes", sub: "Días de viaje pintados y km del mes por chofer, por empresa" },
      // Novedades del PERSONAL, que no es la pantalla «Novedades» del inicio:
      // aquélla deriva tarjetas de datos que ya existen y ésta se escribe a
      // mano. El rótulo largo las separa para el que las ve en el menú.
      { id: "rrhh_novedades", corto: "Nov.", permiso: "ver.rrhh", label: "Novedades del personal", donde: "aca", ruta: "/rrhh/novedades", sub: "Enfermedades, suspensiones y fallecimientos, con su documentación" },
      // #118 (Erick): el contador con consecuencias. A la 4.ª en 12 meses
      // avisa; el apercibimiento y la suspensión los firma una persona.
      // Lo VE también tráfico (P-165, Nico): son infracciones de manejo de los
      // choferes, que tráfico necesita para asignar. Cargar y sancionar siguen
      // siendo de RRHH (cargar.rrhh); la lectura la abre la RLS a ver.trafico.
      { id: "infracciones", corto: "Infr.", permiso: ["ver.rrhh", "ver.trafico"], label: "Infracciones", donde: "aca", ruta: "/rrhh/infracciones", sub: "El contador por chofer: a la 4.ª corresponde apercibimiento" },
      // P-172 (Natalia): los días por antigüedad (CCT 40/89), lo tomado y lo
      // pendiente, por empleado. Con casillero OK para descontar.
      { id: "vacaciones", corto: "Vac.", permiso: "ver.rrhh", label: "Vacaciones", donde: "aca", ruta: "/rrhh/vacaciones", sub: "Días por antigüedad, tomados y pendientes — CCT 40/89" },
      // P-173 (Natalia): el seguimiento médico de los siniestros de la ART, que
      // ella baja del portal como export «ObservacionesYSeguimientos» (una
      // tabla HTML con extensión .xls). El archivo no dice de quién es, así que
      // la persona se elige acá con el buscador y el DNI se pega a cada fila.
      // Dato médico: se mira con ver.rrhh y se carga con cargar.rrhh (la pared
      // es la RLS). Sin `permisoEditar` porque no se edita fila por fila: se
      // sube el archivo, y quien puede subirlo es quien tiene cargar.rrhh.
      { id: "art_seguimiento", corto: "ART", permiso: "ver.rrhh", label: "Seguimiento ART", donde: "aca", ruta: "/rrhh/art", sub: "Notas médicas de los siniestros, del export del portal" },
      // Los descargos de los chóferes (/descargo) y los avisos que satelital
      // deriva a RRHH. Antes sólo llegaban por mail; acá quedan a la vista para
      // que un mail perdido no pierda el descargo.
      { id: "descargos", corto: "Descargos", permiso: ["ver.rrhh", "cargar.rrhh"], sub: "Lo que los chóferes mandan como descargo, tal cual", label: "Descargos", donde: "flotabot", ruta: "/descargos" },
      // Daño no declarado (Nico 30/09, Igna 02/10): un daño que nadie declaró
      // al recibir la unidad. Lo arma Nico por WhatsApp con todo lo que había;
      // Erick coteja, lee el descargo y decide si va el apercibimiento.
      { id: "danos_no_declarados", corto: "Daños", permiso: ["ver.rrhh", "cargar.rrhh"], sub: "Daños sin declarar al recibir la unidad: corresponde o no el apercibimiento", label: "Daños no declarados", donde: "flotabot", ruta: "/danos-no-declarados" },
      // Herramientas a descontar (Nico, 07/10): «personal ve la liquidación
      // final y pregunta si hay que descontarle algo». Qué rompió, qué perdió
      // y qué tiene sin devolver cada persona. El aviso le llega solo por mail.
      { id: "herramientas_a_descontar", corto: "A descontar", permiso: ["ver.rrhh", "cargar.rrhh"], sub: "Qué rompió, perdió o no devolvió cada persona, para la liquidación final", label: "Herramientas a descontar", donde: "flotabot", ruta: "/herramientas-a-descontar" },
    ],
  },

  /* EL PAÑOL ES UN PADRE CON UN HIJO POR SEDE (Igna, 11/09).
   *
   * Dejó de ser «el del puerto»: cada sucursal tiene su pañol, y lo que se
   * pregunta siempre es de UNA sede («¿hay filtros en Río Grande?»). Por eso
   * las sedes son ítems del menú y no pestañas escondidas adentro de una
   * pantalla: lo que se abre es la sede, no el pañol en general.
   *
   * BsAs (Don Torcuato) es la MADRE —tiene el stock y reparte— así que es la
   * única con las tres solapas (Stock · Depósito · Talleres y servicios). Las
   * demás tienen una sola: qué hay, cuándo se cargó y en qué unidad se usó.
   *
   * Los padrones de abajo NO son lo mismo y por eso quedan en su propio grupo:
   * `stock`, `depositos` y `proveedores` (en el menú, «Talleres y servicios»)
   * son las tablas generales del sistema, no el pañol de una sede. Fusionarlos
   * dejaría dos cosas distintas llamadas «Stock» en el mismo menú. */
  {
    universo: "Pañol",
    grupo: "Sedes",
    items: [
      { id: "panol-bsas", corto: "BsAs", permiso: "ver.panol", permisoEditar: "cargar.panol", sub: "La madre: tiene el stock y reparte a las demás", label: "BsAs (Don Torcuato)", donde: "flotabot", ruta: "/panol/bsas" },
      /* La oficina de Nico (08/10): «que sea un pañol madre también; que
         pueda agregar y quitar stock directamente y que figure en "oficina
         de Nico"». Tiene su propio stock y se carga desde la pantalla. */
      { id: "panol-oficina-nico", corto: "Of. Nico", permiso: "ver.panol", permisoEditar: "cargar.panol", sub: "Madre: su propio stock, se carga y se saca desde acá", label: "Oficina de Nico", donde: "flotabot", ruta: "/panol/oficina-nico" },
      { id: "panol-puerto", corto: "Puerto", permiso: "ver.panol", permisoEditar: "cargar.panol", sub: "Qué hay, cuándo se cargó y en qué unidad se usó", label: "Puerto", donde: "flotabot", ruta: "/panol/puerto" },
      { id: "panol-bahia-blanca", corto: "Bahía", permiso: "ver.panol", permisoEditar: "cargar.panol", sub: "Qué hay, cuándo se cargó y en qué unidad se usó", label: "Bahía Blanca", donde: "flotabot", ruta: "/panol/bahia-blanca" },
      { id: "panol-rio-gallegos", corto: "R. Gallegos", permiso: "ver.panol", permisoEditar: "cargar.panol", sub: "Qué hay, cuándo se cargó y en qué unidad se usó", label: "Río Gallegos", donde: "flotabot", ruta: "/panol/rio-gallegos" },
      { id: "panol-rio-grande", corto: "R. Grande", permiso: "ver.panol", permisoEditar: "cargar.panol", sub: "Qué hay, cuándo se cargó y en qué unidad se usó", label: "Río Grande", donde: "flotabot", ruta: "/panol/rio-grande" },
      { id: "panol-ushuaia", corto: "Ushuaia", permiso: "ver.panol", permisoEditar: "cargar.panol", sub: "Qué hay, cuándo se cargó y en qué unidad se usó", label: "Ushuaia", donde: "flotabot", ruta: "/panol/ushuaia" },
      { id: "panol-jow-kon", corto: "Jow Kon", permiso: "ver.panol", permisoEditar: "cargar.panol", sub: "Qué hay, cuándo se cargó y en qué unidad se usó", label: "Jow Kon", donde: "flotabot", ruta: "/panol/jow-kon" },
    ],
  },

  {
    universo: "Pañol",
    grupo: "Padrones",
    items: [
      { id: "stock", permiso: "ver.panol", permisoEditar: "cargar.panol", label: "Stock", donde: "aca", ruta: "/padron/stock", cuenta: "stock", sub: "Repuestos e insumos" },
      { id: "depositos", permiso: "ver.panol", permisoEditar: "cargar.panol", label: "Depósitos", donde: "aca", ruta: "/depositos", cuenta: "depositos" },
      /* El padrón de proveedores vive acá y no en un sector "Comercial" que
       * no existe: a quien le sirve la lista es al que compra.
       *
       * SE LLAMA «TALLERES Y SERVICIOS» DESDE EL 20/10. Nico pidió un sector
       * propio de Proveedores, con las facturas a pagar, y el dueño: «tiene
       * que ser madre y no estar anidado». Dos «Proveedores» en el menú
       * hubieran sido dos puertas con el mismo cartel.
       *
       * Cambia sólo lo que se lee. El id, la ruta y la `cuenta` quedan: la
       * ruta la llevan el buscador, los favoritos y los mensajes ya mandados,
       * e `itemDelPadron` encuentra el permiso por ella.
       *
       * El `alias` es el nombre de antes, que es como la gente la busca: lo
       * miran el casillero «Buscar en el menú» (que no lee el `sub`) y el
       * buscador de arriba. El `sub` dice qué hay adentro, que es más que
       * talleres: la mayoría son marcas y casas de repuestos. */
      { id: "proveedores", permiso: "ver.panol", permisoEditar: "cargar.panol", label: "Talleres y servicios", alias: "proveedores", donde: "aca", ruta: "/padron/proveedores", cuenta: "proveedores", sub: "Proveedores del pañol: repuestos, insumos, talleres y servicios" },
    ],
  },

  {
    /* ── PAÑOL · MOVIMIENTOS ───────────────────────────────────────────
     *
     * Es la pantalla que era «Pañol del puerto» y estaba colgada de Puerto
     * (Igna, 11/09: *«en "puerto" sigue apareciendo pañol del puerto»*). Dejó
     * de ser del puerto el día que el pañol pasó a tener las siete sedes: lo
     * que muestra es TODO lo que salió de Buenos Aires, a cualquiera de ellas.
     *
     * NO SE BORRÓ, SE MOVIÓ, y el motivo es concreto: **es el único lugar
     * donde se anula** un envío o un uso. Las páginas por sede son de mirar.
     * Sacarla del menú se habría llevado puesta la única forma de deshacer un
     * dedazo, y eso no se ve hasta el día que hace falta.
     *
     * DOS JUEGOS DE LLAVES, y con cualquiera alcanza: la gente del puerto la
     * venía usando con `ver.puerto` y el sector ahora es del pañol. Pedir sólo
     * `ver.panol` se la habría sacado de un día para el otro a los que la usan.
     */
    universo: "Pañol",
    grupo: "Movimientos",
    items: [
      { id: "puertopanol", corto: "Movimientos", permiso: ["ver.panol", "ver.puerto"], permisoEditar: ["cargar.panol", "cargar.puerto"], label: "Movimientos del pañol", donde: "aca", ruta: "/puerto/panol", sub: "Todo lo que salió de Buenos Aires, a qué sede llegó y en qué se usó — y lo único que se anula" },
    ],
  },

  {
    /* Presidencia es su propio universo y no un ítem de Dirección: `ver.direccion`
     * lo tienen seis personas y esto lo tienen dos. Un tablero que se llama del
     * presidente colgando de una llave que ya circula es la clase de cosa que un
     * día se da «porque total ya ve Dirección». */
    universo: "Presidencia",
    grupo: "Presidencia",
    items: [
      { id: "presidencia", corto: "Tablero", permiso: "ver.presidencia", label: "Tablero", donde: "aca", ruta: "/presidencia", sub: "Viajes por cliente, semana y sentido" },
    ],
  },

  {
    /* Aduana es su propio universo, por el mismo motivo que Presidencia: acá
     * adentro se emiten documentos que se presentan en la frontera con la
     * firma de la empresa. `ver.direccion` lo tienen seis personas; esto
     * arranca con dos, y cuando haya que sumar a alguien se hace desde la
     * pantalla de Permisos.
     *
     * La ficha fiscal NO es un padrón de clientes paralelo: el padrón vive en
     * `nombre_canonico`. Se administraba desde un panel de Presidencia que se
     * quitó el 05/10/2026: hoy ninguna pantalla lo escribe —los nombres
     * dudosos se le preguntan a Aldo por mail y entran por migración—. Acá se
     * le agregan la razón social, el CUIT y el domicilio que piden el CRT y el
     * MIC. */
    universo: "Aduana",
    grupo: "Aduana",
    items: [
      { id: "aduanacrt", corto: "CRT", permiso: "ver.aduana", permisoEditar: "cargar.aduana", label: "CRT · carta de porte", donde: "aca", ruta: "/aduana/crt", sub: "El documento del tránsito, que se imprime en A4" },
      { id: "aduanamic", corto: "MIC/DTA", permiso: "ver.aduana", permisoEditar: "cargar.aduana", label: "MIC/DTA · uno por camión", donde: "aca", ruta: "/aduana/mic", sub: "Reparte el CRT entre los camiones, con la cuenta controlada" },
      { id: "aduanapapeles", corto: "Papeles", permiso: "ver.aduana", permisoEditar: "cargar.aduana", label: "Papeles del despachante", donde: "aca", ruta: "/aduana/papeles", sub: "El BL y el tránsito de ARCA que llegan solos por mail" },
      { id: "aduanaclientes", corto: "Clientes", permiso: "ver.aduana", permisoEditar: "cargar.aduana", label: "Clientes · datos fiscales", donde: "aca", ruta: "/aduana/clientes", sub: "Razón social, CUIT y domicilio para el CRT y el MIC" },
    ],
  },

  {
    /* Seguros: las pólizas y los siniestros de la flota.
     *
     * CADA UNO CON SU LLAVE (el dueño, 23/10: «Pólizas debería tener un rol
     * aparte que sea ver.polizas y los siniestros ver.siniestros. Pólizas y
     * siniestros los tiene que poder ver satelital por default»). Antes las
     * pólizas colgaban de `ver.aduana` —la llave de los CRT y los MIC— y los
     * siniestros de las tres de tráfico. «Satelital por default» es un O con
     * `cargar.satelital` en cada regla, acá y en la base
     * (20261025150000_ver_polizas_y_ver_siniestros.sql), y no una copia de
     * las llaves: el que entre mañana a Satelital ya las ve.
     *
     * La misma hoja que se abre en Pólizas la responde el bot de WhatsApp con
     * `seguro <patente|interno>`. */
    universo: "Seguros",
    grupo: "Seguros",
    items: [
      { id: "seguros", corto: "Seguros", permiso: PERMISO_POLIZAS, label: "Pólizas de la flota", donde: "aca", ruta: "/seguros", sub: "La póliza de cada unidad, con su vencimiento y la carátula" },
      // Los siniestros, TAMBIÉN acá (Igna, 20/10: «los siniestros van para
      // ella SIEMPRE», por Nora, «Jefa de Seguros y Satelital»). Es la MISMA
      // página de flotabot que la de Satelital (`ruta` idéntica, como el
      // calendario de TCC y RRHH); cambia el `id` porque no puede repetirse
      // y el panel abre por id.
      //
      // ACÁ VA `ver.siniestros` SOLA, y en Satelital `cargar.satelital` sola:
      // con las dos llaves en las dos entradas, todo Satelital vería
      // «Siniestros» dos veces en el rail. Quien tiene las dos (Nora) la ve
      // en los dos lugares, como hasta hoy.
      { id: "seguros_siniestros", corto: "Siniestros", permiso: "ver.siniestros", label: "Siniestros", donde: "flotabot", ruta: "/siniestros", sub: "Los choques de los chóferes, con la foto y el mapa: todos le llegan a Seguros" },
    ],
  },

  {
    /* Fueguino: las encomiendas Buenos Aires → Tierra del Fuego.
     *
     * ── POR QUÉ ES UN UNIVERSO Y NO UNA PANTALLA DE TRÁFICO ────────────────
     *
     * Es otra operación, con otra gente y otro papel. Tráfico mueve camiones
     * de la flota; esto mueve bultos de terceros, se cobra por kilo o por
     * metro cúbico y termina en una guía con la firma de quien la retiró. Y va
     * a crecer hacia adentro —cuenta corriente, tarifas por cliente,
     * liquidación—, así que nace con lugar propio en vez de colgado de un
     * sector que no es el suyo.
     *
     * ── POR QUÉ EL PERMISO SE LLAMA `encomiendas` Y NO `fueguino` ──────────
     *
     * Porque ya existe y ya lo tienen las tres personas que atienden el sector
     * (Igna, Nico y Nico OroZ, desde el 19/08). La PWA del mostrador
     * —`encomiendas.fibot.ar`— lee las mismas tablas con el mismo permiso: es
     * la misma persona de los dos lados. Un `ver.fueguino` aparte sería una
     * segunda lista para mantener, y el día que alguien entra al sector habría
     * que acordarse de las dos. Acá el universo se llama por la empresa y el
     * permiso por la función, y está bien que así sea.
     *
     * ── LO QUE ACÁ NO VA ──────────────────────────────────────────────────
     *
     * Cargar una guía. Eso pasa donde se pesa el bulto, en la PWA, que guarda
     * el escaneo sin señal porque en Río Grande el internet se corta. El hub
     * es la otra mitad: la oficina que mira todo junto. */
    universo: "Fueguino",
    grupo: "Operación",
    items: [
      { id: "fueguinocargas", corto: "Cargas", permiso: "ver.encomiendas", label: "Monitor de cargas", donde: "aca", ruta: "/fueguino/cargas", sub: "Las guías y sus bultos, con la que va partida arriba de todo" },
      /* Las cinco de abajo son el circuito de Cristal traído al hub, pero
         todavía sin construir. Van como "pronto" a propósito: el módulo de
         Cargas de Cristal muestra Consulta de Envíos, Despachos por Cliente,
         Fojas de Ruta, Planillas de Distribución, Repartos — y la gente de
         Fueguino ya los tiene en la cabeza. Mostrar el mapa completo desde el
         día uno (aunque no se toque) dice a dónde vamos y evita que el sector
         se sienta a medias. Ninguna lleva `ruta`: "pronto" no abre nada. */
      { id: "fueguinoretiros", permiso: "ver.encomiendas", label: "Retiros y recolecciones", donde: "pronto", sub: "Lo que hay que ir a buscar antes de que sea una guía" },
      { id: "fueguinoguias", permiso: "ver.encomiendas", label: "Guías", donde: "pronto", sub: "Consulta, valorización y reimpresión — la emisión vive en el mostrador" },
      { id: "fueguinofojas", permiso: "ver.encomiendas", label: "Fojas de ruta", donde: "pronto", sub: "Qué bulto va en qué camión, con precinto, chofer y patente" },
      /* La primera del circuito de Cristal que existe de verdad: toma las fojas
         (.xlsx de Cristal) + el nº de REM y arma las filas del AREF, que hoy se
         copian a mano —~1 h por camión—. Corre en el navegador, sin backend. */
      { id: "fueguinoaref", corto: "AREF", permiso: "ver.encomiendas", label: "AREF (autocompletar)", donde: "aca", ruta: "/fueguino/aref", sub: "Las guías de la foja al Excel del AREF, con el nº de REM estampado" },
      { id: "fueguinomilla", permiso: "ver.encomiendas", label: "Última milla", donde: "pronto", sub: "El reparto en destino, con la posición real del camión" },
    ],
  },

  {
    /* Comercial: lo que toca plata. Hoy va con `ver.encomiendas` como el
       resto, porque son pantallas "pronto" y todavía no muestran ningún
       número. Cuando se construyan de verdad, estas tres nacen con un permiso
       propio y más angosto —`ver.encomiendas.comercial`, como se hizo con
       Aduana— así no todo el que ve encomiendas ve saldos y facturación. La
       llave nueva y su RLS entran en la migración de cada pantalla, no ahora:
       un permiso creado sin nadie que lo tenga y sin nada detrás es una lista
       más para mantener antes de tiempo. */
    universo: "Fueguino",
    grupo: "Comercial",
    items: [
      { id: "fueguinoclientes", permiso: "ver.encomiendas", label: "Clientes", donde: "pronto", sub: "El padrón, con cómo se le cobra a cada cuenta" },
      /* Tarifas: la escala de la carta de TransAmerica cargada de verdad, con
         un cotizador en borrador (la regla de cálculo la confirma Matías). Es
         la segunda del circuito Cristal que existe, después del AREF. */
      { id: "fueguinotarifas", corto: "Tarifas", permiso: "ver.encomiendas", label: "Tarifas", donde: "aca", ruta: "/fueguino/tarifas", sub: "Escala por ruta y cotizador (borrador), con vigencia" },
      { id: "fueguinocc", permiso: "ver.encomiendas", label: "Cuenta corriente", donde: "pronto", sub: "Saldo, pendiente de facturar y límite de crédito" },
      { id: "fueguinofactura", permiso: "ver.encomiendas", label: "Facturación", donde: "pronto", sub: "Guía-factura o carta de porte, según la forma de pago" },
    ],
  },

  {
    universo: "Fueguino",
    grupo: "Consulta",
    items: [
      /* El tracking probablemente termine viviendo fuera del hub —es para el
         cliente final, sin login— pero queda listado para no perderlo del
         mapa. */
      { id: "fueguinotracking", permiso: "ver.encomiendas", label: "Tracking público", donde: "pronto", sub: "El cliente entra con su número y ve dónde está el paquete" },
      { id: "fueguinotablero", permiso: "ver.encomiendas", label: "Tablero", donde: "pronto", sub: "Cumplimiento, tiempos y última milla, en vivo" },
    ],
  },

  {
    /* Los comprobantes: lo que llega por WhatsApp y lo que mandan los
     * proveedores por mail. CON LLAVE (Igna, 07/10, al ver el sector en el
     * menú de Portería): «eso no lo tiene que ver nadie más que los que están
     * autorizados». Hasta ese día iba sin llave (11/09) y recortaba sólo la
     * RLS (`veo_el_comprobante`): nadie leía lo ajeno, pero el sector le
     * aparecía a todo el hub. Son DOS llaves: `ver.comprobantes` entra y
     * ve sólo lo que cargó él; `ver.comprobantes.todos` ve todo (lo de los
     * demás y lo que mandan los proveedores por mail). Lo recorta la base. */
    universo: "Comprobantes",
    grupo: "Comprobantes",
    items: [
      /* Una sola lista con TODO (Nacho, 28/09): lo que se manda por WhatsApp y lo
         que mandan los proveedores por mail, juntos, con un chip de origen. */
      { id: "comprobantes", permiso: ["ver.comprobantes", "ver.comprobantes.todos"], corto: "Facturas", label: "Facturas y remitos", donde: "aca", ruta: "/comprobantes", sub: "Todo junto: lo de WhatsApp y lo que mandan los proveedores por mail" },
    ],
  },

  {
    /* Proveedores (Nico, 19/10; el dueño, 20/10: «tiene que ser madre y no
     * estar anidado»). Sector propio, separado de Comprobantes: las mismas
     * facturas, con su estado de pago —quién la autorizó, si está paga, cómo
     * se paga—. Tres llaves propias que se dan de a uno (`ver.`, `cargar.` y
     * `autorizar.proveedores`); no reusa `aprobar.compras`, que cuelga de
     * Taller. El id NO es "proveedores": ése es el padrón del Pañol. */
    universo: "Proveedores",
    grupo: "Proveedores",
    items: [
      { id: "facturas-a-pagar", permiso: "ver.proveedores", permisoEditar: ["cargar.proveedores", "autorizar.proveedores"], corto: "A pagar", label: "Facturas a pagar", donde: "aca", ruta: "/proveedores/facturas", cuenta: "facturas_a_pagar", ocultarEnCero: true, sub: "Las facturas que llegaron: quién autorizó, si están pagas y cómo se pagan" },
    ],
  },

  {
    /* Mi espacio: el rincón de cada persona. SIN LLAVE: el
       sector existe para todos y la RLS (`dueno = mi_mail()`) recorta a "lo
       tuyo". Cada uno arma sus planillas —estilo Excel, con fórmulas— para
       llevar el registro que quiera, y nosotros nos alimentamos de ese dato
       desde el servidor (service_role saltea la RLS). Los documentos de texto
       libre entran acá en una segunda etapa. */
    universo: "Mi espacio",
    grupo: "Mi espacio",
    items: [
      { id: "misplanillas", corto: "Planillas", label: "Mis planillas", donde: "aca", ruta: "/mi-espacio", sub: "Tu propio Excel con fórmulas: llevá el registro de lo que quieras" },
      { id: "misdocumentos", corto: "Documentos", label: "Mis documentos", donde: "aca", ruta: "/mi-espacio/documentos", sub: "Texto libre: notas, contactos, lo que quieras anotar" },
      { id: "mispendientes", corto: "Pendientes", label: "Mis pendientes", donde: "aca", ruta: "/mi-espacio/pendientes", sub: "Tu lista de lo que tenés que hacer: anotá y tildá cuando esté listo" },
    ],
  },

  {
    /* Automatizaciones (Igna, 03/10/2026): «un área para automatizar flujos,
       en estilo programación por bloques, que todos puedan verlo». SIN LLAVE,
       y no por descuido: «que todos puedan verlo» es el pedido. La RLS deja
       leer todas a cualquiera y tocar sólo al dueño, así que un permiso acá
       sólo serviría para esconderle a alguien cómo están armadas las de los
       demás. Universo propio: no es de Mi espacio (las ven todos) ni de
       ningún sector (las arma cualquiera, para lo suyo). */
    universo: "Automatizaciones",
    grupo: "Automatizaciones",
    items: [
      { id: "automatizaciones", corto: "Automatizar", label: "Automatizaciones", donde: "aca", ruta: "/automatizaciones", sub: "Cuando pasa esto, hacé esto: bloques que corre el bot (mails, planillas, WhatsApp)" },
    ],
  },

  {
    /* YPF: el precio del gasoil D-500 que pagamos en Otamendi, Caleta Olivia.
     * Igna, 04/10/2026: sólo el valor que declara Carlos Becerra (por mail; lo
     * carga FlotaBot). Ya no muestra el genérico por estación de YPF Ruta, así
     * que el `sub` no lo promete. Igna, 06/10/2026: suma Trelew, que es YPF
     * Ruta menos el 4%; Nico, 07/10/2026: el de la estación MICA, sola, sin
     * promediar ni tabla por estación.
     * Universo propio y llave propia (`ver.ypf`):
     * son precios comerciales, confidenciales, y no son el consumo de la flota
     * —eso es `ver.combustible`, otra cosa—. Es de MIRAR: la tabla la llena el
     * bot, acá sólo se ve. */
    universo: "YPF",
    grupo: "YPF",
    items: [
      { id: "ypf", corto: "Gasoil", permiso: "ver.ypf", label: "Precio del gasoil", donde: "aca", ruta: "/ypf", sub: "El D-500 de Otamendi, Caleta Olivia, y el de MICA, Trelew" },
    ],
  },

  {
    /* Peajes: lo que cobró el ente de peajes a cada camión, cruzado con el
     * GPS (FiBOT-Peajes, otra base, que FlotaBot sólo lee). Igna, 15/10/2026.
     * Llave propia (`ver.peajes`): es plata y reclamos al ente. La misma
     * llave abre las consultas de peajes por WhatsApp. */
    universo: "Peajes",
    grupo: "Peajes",
    items: [
      { id: "peajes", corto: "Peajes", permiso: "ver.peajes", permisoEditar: "cargar.peajes", label: "Cobros de peaje", donde: "flotabot", ruta: "/peajes", sub: "Lo cobrado contra el GPS de cada camión, y las banderas para reclamar" },
    ],
  },

  {
    universo: "Dirección",
    grupo: "Dirección",
    items: [
      { id: "nocturnos", corto: "Nocturnos", permiso: "ver.direccion", label: "Infracción nocturna", donde: "flotabot", ruta: "/nocturnos/anual", sub: "Quién circula de noche, y quién repite" },
      { id: "ureaconsumo", corto: "Urea", permiso: "ver.direccion", label: "Quiénes usan urea", donde: "flotabot", ruta: "/adblue?usan=1", sub: "Quiénes usan AdBlue y quiénes no" },
    ],
  },

  {
    universo: "Sistema",
    grupo: "Sistema",
    items: [
      { id: "uso", corto: "Bot", permiso: "ver.sistema", sub: "Quién usa el bot de WhatsApp", label: "Uso del bot", donde: "flotabot", ruta: "/uso" },
      // El espejo de chats: la conversación EXACTA de cada uno con el bot, las
      // dos puntas. «Uso del bot» de arriba cuenta cuánto se usa; esto muestra
      // qué se dijo, para ver si el bot contestó bien.
      { id: "espejo", corto: "Chats", permiso: "ver.sistema", label: "Espejo de chats", donde: "aca", ruta: "/espejo", sub: "La conversación exacta de cada uno con el bot, chofer ↔ Cepi" },
      { id: "accesos", corto: "Accesos", permiso: "ver.sistema", label: "Accesos al hub", donde: "aca", ruta: "/accesos", sub: "Quién usa el hub, y hace cuánto" },
      { id: "alertas", corto: "Alertas", permiso: "ver.sistema", label: "Alertas", donde: "aca", ruta: "/alertas", sub: "Qué avisa el sistema, a quién y cuándo" },
      /* SIN PERMISO, y es la decisión: unir dos PDFs es una herramienta de
       * oficina, no un área. Una llave para esto sería una llave más que a
       * alguien le falta el día que la necesita, y el trabajo pasa entero en
       * su propia máquina — no hay dato de nadie que proteger. Lo que SÍ tiene
       * llave es mirar lo que guardó otro (`ver.pdf.todos`), y eso lo resuelve
       * la base, no el menú. */
      { id: "pdfs", corto: "PDFs", label: "Taller de PDFs", donde: "aca", ruta: "/pdfs", sub: "Trabajar un PDF sin subirlo a ningún lado" },
      { id: "permisos", permiso: "admin.permisos", label: "Permisos", donde: "aca", ruta: "/permisos", sub: "Quién puede ver y hacer qué" },
      /* Las claves del hub: mandar la provisoria y el alta asistida (la clave
       * dictada, para el que no se maneja con mails). Vive en FlotaBot porque
       * ahí están el mail y la service key; acá va el acceso — un link que
       * hay que acordarse de memoria es lo mismo que no estar. */
      { id: "altas", corto: "Altas", permiso: "admin.permisos", label: "Altas del hub", donde: "flotabot", ruta: "/hub/altas", sub: "Claves provisorias y alta asistida" },
    ],
  },
];

/* Los números van con separador de miles al modo de acá: 25.013, no 25,013. */
export function formatearCuenta(n) {
  if (n == null) return null;
  return new Intl.NumberFormat("es-AR").format(n);
}

/* ¿Puede EDITAR esta pantalla?
 *
 * Una sección sin `permisoEditar` no distingue: quien la puede abrir la puede
 * usar. Devuelve `true` mientras los permisos no llegaron, por la misma razón
 * que el rail muestra todo: parpadear con la pantalla en sólo lectura durante
 * medio segundo haría que alguien empiece a escribir y pierda lo tipeado.
 *
 * `permisoEditar` puede ser UNA llave o VARIAS, y con cualquiera alcanza. Sale
 * del padrón de gente: lo pueden corregir los de personal (`cargar.gente`) y
 * los de RRHH (`cargar.rrhh`), que son dos áreas distintas tocando la misma
 * fila. La alternativa era repartir `cargar.gente` a RRHH, y eso les habría
 * abierto de paso todo lo demás que esa llave abre.
 */
export function puedeEditar(item, permisos) {
  if (!item || !item.permisoEditar) return true;
  if (permisos === null || permisos === undefined) return true;
  const pide = Array.isArray(item.permisoEditar)
    ? item.permisoEditar
    : [item.permisoEditar];
  return pide.some((p) => permisos.includes(p));
}

/* ¿Puede VER esta sección? El mismo contrato que `puedeEditar`: `permiso`
 * puede ser UNA llave o VARIAS, y con cualquiera alcanza.
 *
 * Nació con la hoja de viaje (Igna, 31/08): la MIRA RRHH (`ver.rrhh`) y la
 * CARGA alguien que eventualmente no va a ser de RRHH (`cargar.viajes`) — y
 * darle `ver.rrhh` a ese alguien le abriría de paso la nómina y los sueldos,
 * que es exactamente lo que la llave separada vino a evitar. */
export function puedeVer(item, permisos) {
  if (!item) return true;
  if (permisos === null || permisos === undefined) return true;
  /* `permisoTambien`: una llave que hace falta ADEMÁS de alguna de
   * `permiso`. Nació con Siniestros en el universo Seguros (20/10), que
   * desde el 23/10 tiene llave propia y ya no lo usa; queda el contrato,
   * que es genérico y está probado. */
  if (item.permisoTambien && !permisos.includes(item.permisoTambien)) return false;
  if (!item.permiso) return true;
  const pide = Array.isArray(item.permiso) ? item.permiso : [item.permiso];
  return pide.some((p) => permisos.includes(p));
}

/* El ítem del menú de un padrón (`/padron/tractores` → el ítem de Tractores).
 *
 * VIVE ACÁ Y NO EN `Padron.jsx`, por el mismo motivo que `tituloDe`: es lógica
 * del catálogo, y acá se puede probar suelta. Importar la pantalla arrastra el
 * modal, el motor de realtime y el cliente de Supabase — o sea que para
 * comprobar de qué permiso depende un padrón haría falta un `.env`.
 *
 * De acá sale el `permisoEditar` que decide si se dibuja el lápiz. Escrito
 * aparte en la pantalla, el día que se cambie el permiso de una sección el
 * menú diría una cosa y la pantalla otra, y la que manda sería la que nadie
 * mira.
 */
export function itemDelPadron(que, secciones = SECCIONES) {
  return (
    secciones.flatMap((g) => g.items).find((i) => i.ruta === `/padron/${que}`) ||
    null
  );
}

/* Las rutas que NO están en el menú y aun así necesitan encabezado. */
const FUERA_DEL_MENU = {
  /* Salió del menú porque ahora se carga en cada padrón, con el lápiz. La ruta
   * queda viva por los enlaces que ya se mandaron, y por eso conserva título:
   * una pantalla que abre con la marca arriba en vez de su nombre se lee como
   * que no cargó. */
  "/recursos": ["Cargar datos", "Se hace en cada padrón, con el botón Editar"],
  "/cambiar-password": ["Tu contraseña", "Definí la que vas a usar de ahora en más"],
};

/* Qué dice el encabezado en una ruta: `[título, subtítulo]`.
 *
 * VIVE ACÁ Y NO EN EL HEADER porque es lógica del catálogo, y porque acá se
 * puede probar suelta — importar el Header arrastra la sesión y el cliente de
 * Supabase para comprobar un título.
 *
 * Antes el encabezado tenía su propia lista de títulos, y eran dos textos por
 * pantalla que había que acordarse de cambiar juntos. No se acordó nadie: al
 * renombrar "Recursos" a "Cargar datos", el menú decía una cosa y la cabecera
 * de esa misma pantalla decía la otra. No falla y no rompe nada — sólo hace
 * dudar de si uno abrió lo que quería abrir.
 */
/* Qué ítem del catálogo corresponde a una dirección.
 *
 * ESTÁ APARTE PORQUE LO PREGUNTAN DOS COSAS: el encabezado quiere el rótulo y
 * el rail quiere el universo, para no plegar el sector de la pantalla abierta.
 * Con la búsqueda escrita en cada uno, el día que se agregue una forma de ruta
 * —otro detalle, otro prefijo— una de las dos se quedaría sin encontrarla, y
 * el síntoma sería un menú que no marca dónde estás.
 */
export function itemDeRuta(pathname, secciones = SECCIONES, search = "") {
  const items = secciones.flatMap((g) =>
    g.items.map((i) => ({ ...i, universo: g.universo, grupo: g.grupo })),
  );
  if (pathname.startsWith("/panel/")) {
    return items.find((i) => i.id === pathname.slice("/panel/".length)) || null;
  }
  /* LA SOLAPA CUENTA PARA SABER DÓNDE ESTÁS.
   *
   * La agenda de turnos vive en `/rto?vista=agenda`: es una vista de la
   * pantalla de RTO y no una dirección propia, y eso está bien. Pero
   * `useLocation().pathname` no trae la query, así que `/rto?vista=agenda`
   * matcheaba contra el ítem `/rto` y la pantalla se presentaba como «RTO de
   * tractores — Revisión técnica de los camiones» mientras se anotaba el
   * psicofísico de un CHOFER. Con el ítem de al lado prendido al mismo tiempo.
   *
   * Se prueba primero la coincidencia CON la query, que es la más específica.
   * Un `?vista=buscar` —una solapa que no está en el menú— no matchea acá y
   * cae abajo, en `/rto`, que es lo correcto: esa solapa sí es del RTO. */
  const completa = pathname + (search || "");
  return (
    items.find((i) => i.ruta && i.ruta.includes("?") && i.ruta === completa) ||
    items.find((i) => i.ruta === pathname) ||
    // Los detalles (/viajes/7) heredan el título de su sección. Gana la ruta
    // MÁS LARGA que sea prefijo: con la primera que matchee, una ruta corta
    // le roba el título a una larga que empieza igual.
    items
      .filter((i) => i.ruta && i.ruta !== "/" && pathname.startsWith(i.ruta + "/"))
      .sort((a, b) => b.ruta.length - a.ruta.length)[0] ||
    null
  );
}

export function tituloDe(pathname, secciones = SECCIONES, search = "") {
  const encontrada = itemDeRuta(pathname, secciones, search);
  if (encontrada) return [encontrada.label, encontrada.sub || ""];
  return FUERA_DEL_MENU[pathname] || null;
}

/* En qué sector está parada la pantalla abierta.
 *
 * Lo usa el rail para no plegar ese universo: un menú que no puede marcar
 * dónde estás se lee como que la navegación se rompió. `null` cuando la
 * dirección no es de ninguna sección —el login, la ficha de una unidad— y ahí
 * no hay nada que forzar. */
export function universoDe(pathname, secciones = SECCIONES, search = "") {
  return itemDeRuta(pathname, secciones, search)?.universo || null;
}

/* Cuál de los ítems del menú es el que está abierto, para que el rail lo marque.
 *
 * NO alcanza con el `isActive` de `NavLink`: react-router compara sólo el path,
 * así que en `/rto?vista=agenda` daba activo para «Agenda de turnos» Y para
 * «RTO de tractores» a la vez — se prendían los dos y ninguno de los dos decía
 * la verdad. Se resuelve preguntando por el ítem ganador, que es el mismo que
 * decide el título: un solo lugar, y no se pueden contradecir. */
export function idActivo(pathname, search = "", secciones = SECCIONES) {
  return itemDeRuta(pathname, secciones, search)?.id || null;
}

/* Buscar una sección por id, para que una pantalla pueda preguntar por lo suyo
 * sin copiarse la clave del permiso — copiarla es lo que hace que renombrar un
 * permiso deje pantallas mirando una clave que ya no existe, y en silencio.
 *
 * DEVUELVE TAMBIÉN EL UNIVERSO Y EL GRUPO, que es lo que necesita la home para
 * agrupar sus tarjetas por sector. Sin eso, Novedades tendría que recorrer
 * `SECCIONES` por su cuenta —que es lo que hacía— y quedarían dos búsquedas
 * del mismo ítem en dos archivos. */
export function seccion(id, secciones = SECCIONES) {
  for (const g of secciones) {
    const i = g.items.find((x) => x.id === id);
    if (i) return { ...i, universo: g.universo, grupo: g.grupo };
  }
  return null;
}

/* ── LAS PANTALLAS TAMBIÉN SE BUSCAN ──────────────────────────────────────────
 *
 * Igna, 02/10: el buscador tiene que funcionar «como el Spotlight de una Mac»,
 * y lo primero que hace Spotlight es encontrar aplicaciones. Escribir «multas»,
 * «previsión» o «cajas» lleva a la pantalla, sin saber en qué grupo del menú
 * vive.
 *
 * Sólo las que la persona puede abrir (`puedeVer`, la misma regla del menú) y
 * no las «pronto», que no tienen a dónde ir. Cada palabra tiene que aparecer
 * en el nombre, el `alias`, el subtítulo o el grupo; primero las que empiezan con lo
 * escrito, después las que lo tienen en el nombre, al final las del
 * subtítulo. La ruta es la misma que arma el menú (`Rail`): las de FlotaBot
 * van por `/panel/<id>`. */
const sinTildes = (t) =>
  String(t ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export function pantallasQueCoinciden(texto, permisos, secciones = SECCIONES, tope = 5) {
  const q = sinTildes(texto).trim();
  if (q.length < 2) return [];
  const palabras = q.split(/\s+/).filter(Boolean);
  const salida = [];
  const vistas = new Set();
  for (const g of secciones) {
    for (const it of g.items || []) {
      if (!it.ruta || it.donde === "pronto" || !puedeVer(it, permisos)) continue;
      const nombre = sinTildes(`${it.label} ${it.corto || ""}`);
      // El `alias` (el nombre de antes, ver menuBuscar.js) también cuenta.
      const alias = sinTildes([].concat(it.alias || []).join(" "));
      const todo = `${nombre} ${alias} ${sinTildes(it.sub)} ${sinTildes(g.grupo)} ${sinTildes(g.universo)}`;
      if (!palabras.every((w) => todo.includes(w))) continue;
      const destino = it.donde === "flotabot" ? `/panel/${it.id}` : it.ruta;
      /* LA MISMA PÁGINA, UNA VEZ. Las de flotabot van por `/panel/<id>`, y
       * dos ítems con la misma `ruta` en dos universos (Siniestros en
       * Satelital y en Seguros) tienen distinto id: sin esto el buscador
       * mostraba «Siniestros» dos veces. Se compara por la página de
       * flotabot, y gana la primera que la persona puede abrir. */
      const pagina = it.donde === "flotabot" ? `flotabot:${it.ruta}` : destino;
      if (vistas.has(pagina)) continue;
      vistas.add(pagina);
      const peso = sinTildes(it.label).startsWith(q) ? 0
        : palabras.every((w) => nombre.includes(w)) ? 1 : 2;
      salida.push({ id: it.id, label: it.label, sub: it.sub || "",
                    grupo: g.grupo || g.universo || "", destino, peso });
    }
  }
  salida.sort((a, b) => a.peso - b.peso || a.label.localeCompare(b.label, "es"));
  return salida.slice(0, tope);
}
